<template>
  <section>
    <header class="flex items-center space-x-2 mb-6">
        <span
            class="relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white bg-hurst-dark-orange"
            aria-hidden="true">
            2
        </span>
      <h2 class="text-base md:text-22xl text-hurst-light-blue font-semibold">Billing Address</h2>
    </header>
    <div v-if="errors.error" class="rounded-md bg-red-50 p-4 mb-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-5 text-red-400">
            <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">There was an error processing your request</h3>
          <div class="mt-2 text-sm text-red-700">
            <ul role="list" class="list-disc space-y-1 pl-5">
              <li>{{ errors.error }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <form @submit.prevent="updateBillingAddress" class="space-y-6">
      <p v-if="errors.hasOwnProperty('address')" class="text-red-600 ">{{ errors.address }}</p>
      <div class="2xl:w-1/2">
        <label for="address1"
               class="block text-sm font-medium leading-5 text-hurst-blue"><sup class="text-red-500">*</sup>Street Address</label>
        <div class="mt-1 relative rounded border-hurst-blue shadow-sm">
          <input id="address1"
                 type="text"
                 v-model="address.addressLine1"
                 required
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('addressLine1')">{{ errors.addressLine1[0] }}</p>
      </div>
      <div class="2xl:w-1/2">
        <label for="address2"
               class="block text-sm font-medium leading-5 text-hurst-blue">Suite/Apt#/Other</label>
        <div class="mt-1 relative rounded border-hurst-blue shadow-sm">
          <input id="address2"
                 type="text"
                 v-model="address.addressLine2"
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('addressLine2')">{{ errors.addressLine2[0] }}</p>
      </div>
      <div class="2xl:w-1/2">
        <div class="relative rounded border-hurst-blue shadow-sm">
          <label for="country" class="block text-sm font-medium leading-5 text-hurst-blue">
            <sup class="text-red-500">*</sup>Country
          </label>
          <select aria-label="Country"
                  id="country"
                  v-model="address.country"
                  @change="getStateProvinces"
                  required
                  class="form-select mt-1 relative block w-full rounded border-hurst-blue focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
            <option :value="null">Select Your Country</option>
            <option value="Canada">Canada</option>
            <option value="United States">United States</option>
            <option disabled="true">------------------------</option>
            <template v-for="country in countries">
              <option v-if="country.country !== 'United States' && country.country !== 'Canada' "
                      :key="country.abbreviation"
                      :value="country.country">
                {{ country.country }}
              </option>
            </template>
          </select>
        </div>
        <p v-if="errors.hasOwnProperty('country')" class="text-red-600">{{ errors.country[0] }}</p>
      </div>
      <div class="2xl:w-1/2">
        <label for="city" class="block text-sm font-medium leading-5 text-hurst-blue">
          <sup class="text-red-500">*</sup>City
        </label>
        <div class="mt-1 relative rounded border-hurst-blue shadow-sm">
          <input id="city"
                 type="text"
                 v-model="address.city"
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('city')" class="text-red-600">{{ errors.city[0] }}</p>
      </div>
      <div class="2xl:w-1/2">
        <div class="relative rounded border-hurst-blue shadow-sm">
          <label for="stateProvince" class="block text-sm font-medium leading-5 text-hurst-blue">
            <sup class="text-red-500">*</sup>
            State/Province
          </label>
          <select aria-label="State or Province"
                  id="stateProvince"
                  v-model="address.state"
                  class="form-select mt-1 relative block w-full rounded border-hurst-blue focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
            <option v-if="address.country === ''" disabled :value="null">Select Your Country First</option>
            <option v-else disabled :value="null">Select Your State or Province</option>
            <option v-for="province in provinces"
                    :value="province.abbreviation"
                    :key="province.abbreviation">
              {{ province.province }}
            </option>
          </select>
        </div>
        <p v-if="errors.hasOwnProperty('state')" class="text-red-600">{{ errors.state[0] }}</p>
      </div>
      <div class="2xl:w-1/2">
        <label for="postalCode" class="block text-sm font-medium leading-5 text-hurst-blue">
          <sup class="text-red-500">*</sup>Zip/Postal Code
        </label>
        <div class="mt-1 relative rounded border-hurst-blue shadow-sm">
          <input id="postalCode"
                 type="text"
                 v-model="address.zip"
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('zip')" class="text-red-600">{{ errors.zip[0] }}</p>
      </div>
      <div>
        <cart-button type="submit">
          Continue
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </cart-button>
      </div>
    </form>
  </section>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import CartButton from "./CartButton.vue";
import Swal from 'sweetalert2';

const {mapState, mapActions} = createNamespacedHelpers('cart');

export default {
  name: "BillingAddressForm",
  components: {CartButton},

  props: {},

  created() {

    axios.get('/ajax/countries').then((resp) => {
      this.countries = resp.data;
    });

    this.address.addressLine1 = this.user.addressLine1;
    this.address.addressLine2 = this.user.addressLine2;
    this.address.country = this.user.country;
    this.address.city = this.user.city;
    this.address.state = this.user.state;
    this.address.zip = this.user.zip;
    if (this.address.country) {
      this.getStateProvinces()
    }
  },

  computed: {
    ...mapState({
      cart: state => state.cart,
      user: state => state.user
    })
  },

  data() {
    return {
      address: {
        addressLine1: null,
        addressLine2: null,
        country: null,
        city: null,
        state: null,
        zip: null,
      },
      countries: [],
      errors: [],
      provinces: [],
      updatingAddress: false
    }
  },

  methods: {
    ...mapActions(['setUser', 'setCart']),

    async getStateProvinces() {
      const resp = await axios.get(`/ajax/countries/${this.address.country}`);
      this.provinces = resp.data;
    },

    async updateBillingAddress() {
      this.updatingAddress = true;
      Swal.fire({
        icon: 'info',
        title: 'Saving your information',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });
      try {
        const response = await axios.put(`/api/users/${this.user.userId}/address`, this.address);
        this.setUser(response.data);
        const taxResponse = await axios.put(`/api/cart/${this.cart.cartId}/tax`);
        this.setCart(taxResponse.data);
        this.updatingAddress = false;
        Swal.close();
        this.$emit('address-updated');
      } catch (e) {
        this.errors = e.response.data;
        this.updatingAddress = false
        Swal.close();
      }
    },

    useShippingAddress() {
      this.address = {...this.cart.shippingAddress};
      this.address.country = (this.countries.filter(country => country.iso === this.cart.shippingAddress.country))[0].country;
      this.getStateProvinces();
      this.updateBillingAddress();
    }
  }
}
</script>

<style scoped>

</style>